import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Devider,
  Flex,
  List,
  MediaDragAndDropContext,
  Pagination,
  PaginationInfoLabel,
  SearchContext,
  Spinner,
  Text,
} from 'dash-ui';
import { useTheme } from 'styled-components';

import { useGetPropertyByIdQuery } from 'gql/generated.graphql';
import { BulkActionsContext } from 'common/contexts/bulkActionsContext/bulkActionsContext';

import MediaFileItem from './components/MediaFileItem/MediaFileItem';
import { ListWrapper, EmptyContainer } from './MediaFilesList.styles';
import { IMediaFilesListProps } from './MediaFilesList.interfaces';

const MediaFilesList = ({
  propertyId,
  folderId,
  isSlideshowFolder,
  media,
  total,
  setFileToPreviewId,
}: IMediaFilesListProps) => {
  const { t } = useTranslation(['common', 'pages']);
  const { setMediaIds } = useContext(MediaDragAndDropContext);
  const { data: propertyData, loading: propertyLoading } =
    useGetPropertyByIdQuery({ variables: { propertyId } }) || {};
  const { slideshowMediaFolderId } = propertyData?.getPropertyById || {};
  const { searchSlug } = useContext(SearchContext);
  const { selectMedia, checkIsSelected, mediaMapRef, setIsAllSelected } =
    useContext(BulkActionsContext);
  const theme = useTheme();

  const loading = propertyLoading;

  const onItemDrop = (dragItemId: string, hoverItemId: string) => {
    setMediaIds({ mediaIdToMove: dragItemId, mediaIdToReplace: hoverItemId });
  };

  function getPlaceholderText() {
    if (searchSlug) return t`common:placeholders.noSearchResults`;
    if (folderId && isSlideshowFolder)
      return t`pages:propertyPage.noFilesPlaceholder`;

    return t`pages:propertyMediaFolderPage.noFilesPlaceholder`;
  }

  useEffect(() => {
    mediaMapRef.current = media.files.reduce((obj, item) => {
      // @ts-ignore
      obj[item._id] = item;
      return obj;
    }, {});
  }, [media, mediaMapRef]);

  return (
    <Box w="100%">
      <ListWrapper px={6} pos="relative">
        {media.folders.length > 0 && (
          <Box>
            <Box mt={4} mb={6}>
              <Text medium color={theme.colors.n70}>
                {t('common:labels.folders', { number: total.foldersTotal })}
              </Text>
            </Box>
            <List data={media.folders} containerClassName="list">
              {(item) => (
                <MediaFileItem
                  media={item}
                  deletable={slideshowMediaFolderId !== item._id}
                />
              )}
            </List>
          </Box>
        )}
        <Box my={6}>
          <Text medium color={theme.colors.n70}>
            {t('common:labels.files', { number: total.filesTotal })}
          </Text>
        </Box>
        {media.files.length > 0 ? (
          <List
            data={media.files}
            containerClassName="list"
            isDraggable={true}
            onItemDrop={onItemDrop}
          >
            {(item) => {
              const checked = checkIsSelected(item._id);
              return (
                <MediaFileItem
                  onClick={() => setFileToPreviewId(item._id)}
                  media={item}
                  selectItemProps={{
                    onSelect: (checked) => {
                      selectMedia([{ id: item._id, value: checked }]);
                      if (!checked) {
                        setIsAllSelected(checked);
                      }
                    },
                    checked,
                  }}
                />
              );
            }}
          </List>
        ) : (
          <EmptyContainer
            py={24}
            w="100%"
            justifyContent="center"
            alignItems="center"
          >
            <Box w={440}>
              <Text color={theme.colors.n70}>{getPlaceholderText()}</Text>
            </Box>
          </EmptyContainer>
        )}
        {loading && <Spinner size={32} fullContent />}
      </ListWrapper>
      <Devider mt={2} />
      <Flex p={6} justifyContent="space-between" alignItems="center">
        <PaginationInfoLabel
          label={t('pages:propertyPage.paginationMediaLabel')}
          color={theme.colors.n70}
        />
        <Pagination
          previousButtonLabel={t('common:labels.previous')}
          nextButtonLabel={t('common:labels.next')}
        />
      </Flex>
    </Box>
  );
};

export default MediaFilesList;
